import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Package, CheckCircle, XCircle, Truck, Clock, Building2, Ban, Trash2, Download, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shipments = () => {
  const { user } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [formData, setFormData] = useState({
    sscc: '',
    pallet_id: '',
    device_id: '',
    receiver_id: '',
    origin: '',
    destination: '',
    expected_temp_min: -20.0,
    expected_temp_max: 8.0,
    service_profile: 'full_traceability',
    order_id: '',
    customer_ref: '',
  });

  const queryClient = useQueryClient();

  // Fetch shipments
  const { data: shipments = [], isLoading } = useQuery({
    queryKey: ['shipments'],
    queryFn: async () => {
      const response = await axios.get(`${API}/shipments`);
      return response.data;
    },
    refetchInterval: 5000,
  });

  // Fetch receiver organizations for dropdown
  const { data: receiverOrgs = [] } = useQuery({
    queryKey: ['receiver-orgs'],
    queryFn: async () => {
      const res = await axios.get(`${API}/organizations`);
      return res.data.filter(o => o.type === 'receiver');
    },
    enabled: ['pallet_pool', 'owner', 'superadmin'].includes(user?.role),
  });

  // Fetch devices for dropdown
  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await axios.get(`${API}/devices`);
      return response.data;
    },
    enabled: ['pallet_pool', 'owner', 'superadmin'].includes(user?.role),
  });

  // Create shipment mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/shipments`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipments']);
      setIsDialogOpen(false);
      setFormData({
        sscc: '',
        pallet_id: '',
        device_id: '',
        receiver_id: '',
        origin: '',
        destination: '',
        expected_temp_min: -20.0,
        expected_temp_max: 8.0,
        service_profile: 'full_traceability',
        order_id: '',
        customer_ref: '',
      });
      toast.success('Shipment created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create shipment');
    },
  });

  const [etaHours, setEtaHours] = useState('');
  const [activatingShipmentId, setActivatingShipmentId] = useState(null);

  // Activate shipment mutation
  const activateMutation = useMutation({
    mutationFn: async ({ shipmentId, eta_hours }) => {
      const response = await axios.post(`${API}/shipments/${shipmentId}/activate`, {
        transporter_id: user.id,
        eta_hours: eta_hours ? parseFloat(eta_hours) : null,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipments']);
      toast.success('Shipment activated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to activate shipment');
    },
  });

  // Deliver shipment mutation
  const deliverMutation = useMutation({
    mutationFn: async (shipmentId) => {
      const response = await axios.post(`${API}/shipments/${shipmentId}/deliver`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipments']);
      toast.success('Shipment marked as delivered');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to mark as delivered');
    },
  });

  // Accept shipment mutation
  const acceptMutation = useMutation({
    mutationFn: async ({ shipmentId, accepted, notes }) => {
      const response = await axios.post(`${API}/shipments/${shipmentId}/accept`, {
        accepted,
        notes,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipments']);
      setSelectedShipment(null);
      toast.success('Shipment decision recorded');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to accept/reject shipment');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (shipmentId) => (await axios.post(`${API}/shipments/${shipmentId}/cancel`)).data,
    onSuccess: () => { queryClient.invalidateQueries(['shipments']); toast.success('Shipment cancelled, device detached'); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to cancel'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (shipmentId) => (await axios.delete(`${API}/shipments/${shipmentId}`)).data,
    onSuccess: () => { queryClient.invalidateQueries(['shipments']); toast.success('Shipment deleted'); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to delete'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      expected_temp_min: parseFloat(formData.expected_temp_min),
      expected_temp_max: parseFloat(formData.expected_temp_max),
    };
    // Remove empty optional fields
    if (!payload.order_id) delete payload.order_id;
    if (!payload.customer_ref) delete payload.customer_ref;
    createMutation.mutate(payload);
  };

  const handleExportCSV = async (shipmentId, sscc) => {
    try {
      const res = await axios.get(`${API}/shipments/${shipmentId}/export/csv`);
      const blob = new Blob([res.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleExportReport = async (shipmentId) => {
    try {
      const res = await axios.get(`${API}/shipments/${shipmentId}/export/report`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_report_${shipmentId}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Compliance report exported');
    } catch {
      toast.error('Report generation failed');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getStatusColor = (status) => {
    const colors = {
      created: 'bg-slate-100 text-slate-700',
      activated: 'bg-blue-100 text-blue-700',
      in_transit: 'bg-purple-100 text-purple-700',
      delivered: 'bg-orange-100 text-orange-700',
      accepted: 'bg-green-100 text-green-700',
      completed: 'bg-green-100 text-green-700',
      geofence_entered: 'bg-yellow-100 text-yellow-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-red-50 text-red-600',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'created': return <Package className="w-4 h-4" />;
      case 'activated': case 'in_transit': return <Truck className="w-4 h-4" />;
      case 'delivered': return <Clock className="w-4 h-4" />;
      case 'accepted': case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'geofence_entered': return <Package className="w-4 h-4" />;
      case 'cancelled': return <Ban className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="shipments-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Shipments</h2>
          <p className="text-slate-600 mt-1">
            {user?.role === 'pallet_pool' && 'Manage your fleet shipments'}
            {user?.role === 'transporter' && 'View and activate available shipments'}
            {user?.role === 'customer' && 'Track incoming deliveries'}
          </p>
        </div>
        {['pallet_pool', 'owner', 'superadmin'].includes(user?.role) && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-shipment-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Shipment
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Create New Shipment</DialogTitle>
                <DialogDescription>
                  Register a new shipment for tracking
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="shipment-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sscc">SSCC</Label>
                    <Input
                      id="sscc"
                      name="sscc"
                      data-testid="sscc-input"
                      placeholder="073456789012345678"
                      value={formData.sscc}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pallet_id">Pallet ID</Label>
                    <Input
                      id="pallet_id"
                      name="pallet_id"
                      data-testid="pallet-id-input"
                      placeholder="PLT001"
                      value={formData.pallet_id}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Device</Label>
                  <Select value={formData.device_id} onValueChange={v => setFormData(p => ({ ...p, device_id: v }))}>
                    <SelectTrigger data-testid="device-select">
                      <SelectValue placeholder="Select a device" />
                    </SelectTrigger>
                    <SelectContent>
                      {devices.filter(d => d.status === 'active').map(d => (
                        <SelectItem key={d.device_id} value={d.device_id}>{d.name} ({d.device_id})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Receiver</Label>
                  <Select value={formData.receiver_id} onValueChange={v => setFormData(p => ({ ...p, receiver_id: v }))}>
                    <SelectTrigger data-testid="receiver-select">
                      <SelectValue placeholder="Select a receiver" />
                    </SelectTrigger>
                    <SelectContent>
                      {receiverOrgs.map(o => (
                        <SelectItem key={o.id} value={o.id}>
                          <span className="flex items-center gap-2">
                            <Building2 className="w-3 h-3" /> {o.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="origin">Origin</Label>
                    <Input
                      id="origin"
                      name="origin"
                      data-testid="origin-input"
                      placeholder="Oslo Warehouse"
                      value={formData.origin}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="destination">Destination</Label>
                    <Input
                      id="destination"
                      name="destination"
                      data-testid="destination-input"
                      placeholder="Bergen Store"
                      value={formData.destination}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expected_temp_min">Min Temp (°C)</Label>
                    <Input
                      id="expected_temp_min"
                      name="expected_temp_min"
                      data-testid="temp-min-input"
                      type="number"
                      step="0.1"
                      value={formData.expected_temp_min}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expected_temp_max">Max Temp (°C)</Label>
                    <Input
                      id="expected_temp_max"
                      name="expected_temp_max"
                      data-testid="temp-max-input"
                      type="number"
                      step="0.1"
                      value={formData.expected_temp_max}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Service Profile</Label>
                  <Select value={formData.service_profile} onValueChange={v => setFormData(p => ({ ...p, service_profile: v }))}>
                    <SelectTrigger data-testid="service-profile-select">
                      <SelectValue placeholder="Select service profile" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="temperature">Temperature only</SelectItem>
                      <SelectItem value="location">Location only</SelectItem>
                      <SelectItem value="full_traceability">Full traceability</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="order_id">Order ID (optional)</Label>
                    <Input
                      id="order_id"
                      name="order_id"
                      data-testid="order-id-input"
                      placeholder="PO-2026-001"
                      value={formData.order_id}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_ref">Customer Ref (optional)</Label>
                    <Input
                      id="customer_ref"
                      name="customer_ref"
                      data-testid="customer-ref-input"
                      placeholder="REF-12345"
                      value={formData.customer_ref}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    data-testid="cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    data-testid="submit-shipment-btn"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Shipment'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Shipments List */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-500">Loading shipments...</p>
        </div>
      ) : shipments.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No shipments found</h3>
              <p className="text-slate-600 mb-6">
                {user?.role === 'pallet_pool' && 'Create your first shipment to start tracking'}
                {user?.role === 'transporter' && 'No available shipments at the moment'}
                {user?.role === 'customer' && 'No incoming shipments'}
              </p>
              {user?.role === 'pallet_pool' && (
                <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-create-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Shipment
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shipments.map((shipment) => (
            <Card key={shipment.id} data-testid={`shipment-${shipment.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">SSCC: {shipment.sscc}</CardTitle>
                    <CardDescription className="mt-1">
                      Pallet: {shipment.pallet_id}
                    </CardDescription>
                  </div>
                  <Badge className={getStatusColor(shipment.status)}>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(shipment.status)}
                      <span className="capitalize">{shipment.status.replace('_', ' ')}</span>
                    </div>
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Origin:</span>
                    <span className="font-medium text-slate-900">{shipment.origin}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Destination:</span>
                    <span className="font-medium text-slate-900">{shipment.destination}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Temp Range:</span>
                    <span className="font-medium text-slate-900">
                      {shipment.expected_temp_min}°C - {shipment.expected_temp_max}°C
                    </span>
                  </div>
                  {shipment.activated_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Activated:</span>
                      <span className="font-medium text-slate-900">
                        {new Date(shipment.activated_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {shipment.service_profile && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Profile:</span>
                      <span className="font-medium text-slate-900 capitalize">
                        {shipment.service_profile.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  {shipment.order_id && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Order:</span>
                      <span className="font-medium text-slate-900">{shipment.order_id}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200">
                  {user?.role === 'transporter' && shipment.status === 'created' && (
                    <Button
                      className="w-full"
                      data-testid={`activate-${shipment.id}`}
                      onClick={() => activateMutation.mutate(shipment.id)}
                      disabled={activateMutation.isPending}
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Activate Shipment
                    </Button>
                  )}

                  {user?.role === 'transporter' &&
                    shipment.transporter_id === user.id &&
                    (shipment.status === 'activated' || shipment.status === 'in_transit') && (
                      <Button
                        className="w-full"
                        data-testid={`deliver-${shipment.id}`}
                        onClick={() => deliverMutation.mutate(shipment.id)}
                        disabled={deliverMutation.isPending}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark as Delivered
                      </Button>
                    )}

                  {user?.role === 'customer' && shipment.status === 'delivered' && (
                    <div className="space-y-2">
                      <Button
                        className="w-full"
                        variant="default"
                        data-testid={`accept-${shipment.id}`}
                        onClick={() =>
                          acceptMutation.mutate({
                            shipmentId: shipment.id,
                            accepted: true,
                            notes: 'Temperature compliant',
                          })
                        }
                        disabled={acceptMutation.isPending}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept
                      </Button>
                      <Button
                        className="w-full"
                        variant="destructive"
                        data-testid={`reject-${shipment.id}`}
                        onClick={() =>
                          acceptMutation.mutate({
                            shipmentId: shipment.id,
                            accepted: false,
                            notes: 'Temperature exceeded limits',
                          })
                        }
                        disabled={acceptMutation.isPending}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}

                  {(shipment.status === 'accepted' || shipment.status === 'rejected') && (
                    <div className="text-sm text-slate-600">
                      <p className="font-medium">
                        {shipment.status === 'accepted' ? 'Accepted' : 'Rejected'}
                      </p>
                      {shipment.acceptance_notes && (
                        <p className="mt-1 text-xs">{shipment.acceptance_notes}</p>
                      )}
                    </div>
                  )}

                  {/* Owner actions: Cancel / Delete */}
                  {['owner', 'superadmin'].includes(user?.role) && !['completed', 'cancelled'].includes(shipment.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => { if (window.confirm(`Cancel shipment ${shipment.sscc}? This will detach the device.`)) cancelMutation.mutate(shipment.id); }}
                      disabled={cancelMutation.isPending}
                      data-testid={`cancel-shipment-${shipment.id}`}
                    >
                      <Ban className="w-4 h-4 mr-2" /> Stop / Cancel
                    </Button>
                  )}
                  {['owner', 'superadmin'].includes(user?.role) && ['created', 'cancelled', 'completed'].includes(shipment.status) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-1 text-red-500 hover:bg-red-50"
                      onClick={() => { if (window.confirm(`Permanently delete shipment ${shipment.sscc}?`)) deleteMutation.mutate(shipment.id); }}
                      disabled={deleteMutation.isPending}
                      data-testid={`delete-shipment-${shipment.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  )}
                  {/* Export actions */}
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => handleExportCSV(shipment.id, shipment.sscc)}
                      data-testid={`export-csv-${shipment.id}`}
                    >
                      <Download className="w-3 h-3 mr-1" /> CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => handleExportReport(shipment.id)}
                      data-testid={`export-report-${shipment.id}`}
                    >
                      <FileText className="w-3 h-3 mr-1" /> Report
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Shipments;
